import type {Options, SeriesOptionsType} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {axisLabelFormat, escapeHtml, tooltipHtml} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer, MEASURES_CHANNEL} from '../types';

const FILL_OPACITY = 0.15;
// Highcharts' default 85 % leaves no room for spoke labels on the sides of the chart; a narrow card needs even less
const PANE_SIZE = '70%';
const NARROW_PANE_SIZE = '45%';
const NARROW_WIDTH = 400;

const renderRadar: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const x = typeof spec.encoding.x === 'string' ? spec.encoding.x : MEASURES_CHANNEL;
    const seriesChannel = typeof spec.encoding.series === 'string'
        ? spec.encoding.series
        : x === MEASURES_CHANNEL ? undefined : MEASURES_CHANNEL;
    const filled = spec.options.filled === true;

    // A total is a different kind of value than the members it sums up; as a spoke it would only distort the shape.
    const xItems = index.items(x).filter(item => item.member?.role !== 'total' && item.member?.role !== 'missing');
    const seriesItems = seriesChannel ? index.items(seriesChannel) : [undefined];

    const series: SeriesOptionsType[] = seriesItems.map((seriesItem, seriesIndex) => {
        const seriesMeasure = seriesItem?.measure ?? cube.measures[0];
        const color = itemColor(seriesItem?.member, seriesMeasure, seriesIndex, ctx);

        const data = xItems.map(xItem => {
            const measure = xItem.measure ?? seriesMeasure;
            const positions: Record<string, number> = {};
            if (x !== MEASURES_CHANNEL) {
                positions[x] = xItem.index;
            }
            if (seriesChannel && seriesChannel !== MEASURES_CHANNEL && seriesItem) {
                positions[seriesChannel] = seriesItem.index;
            }
            const cell = index.value(positions, measure.id);
            const lowBase = isLowBase(cube, cell);

            return {
                y: cell?.value ?? null,
                ...(lowBase ? lowBaseAttributes('line', color, ctx) : {}),
                custom: {
                    count: cell?.count ?? 0,
                    lowBase,
                    tooltip: tooltipHtml({
                        heading: [seriesItem && seriesItem.label !== xItem.label ? seriesItem.label : '', xItem.label],
                        color,
                        measure,
                        cell,
                        lowBase,
                    }, ctx),
                },
            };
        });

        return {
            type: filled ? 'area' : 'line',
            id: seriesItem?.id ?? seriesMeasure.id,
            name: escapeHtml(seriesItem?.label ?? seriesMeasure.label),
            color,
            ...(filled ? {fillOpacity: FILL_OPACITY} : {}),
            data,
        } as SeriesOptionsType;
    });

    const measure = cube.measures[0];
    const max = measure?.scale?.max ?? (measure?.metric === 'share' ? 100 : undefined);
    // Negative values (e.g. NPS) would collapse to the centre on a 0-based axis, so the axis is left to Highcharts then
    const hasNegative = series.some(item => ((item as any).data as {y: number | null}[]).some(point => point.y !== null && point.y < 0));
    const options: Options = {
        chart: {polar: true, type: filled ? 'area' : 'line'},
        title: {text: undefined},
        credits: {enabled: false},
        pane: {size: PANE_SIZE},
        xAxis: {
            categories: xItems.map(item => escapeHtml(item.label)),
            tickmarkPlacement: 'on',
            lineWidth: 0,
        },
        yAxis: {
            title: {text: undefined},
            gridLineInterpolation: 'polygon',
            lineWidth: 0,
            ...(hasNegative ? {} : {min: 0}),
            ...(max !== undefined ? {max} : {}),
            labels: {format: axisLabelFormat(measure)},
        },
        legend: {enabled: seriesItems.length > 1},
        tooltip: {shared: false, headerFormat: '', pointFormat: '{point.custom.tooltip}'},
        series,
        responsive: {rules: [{condition: {maxWidth: NARROW_WIDTH}, chartOptions: {pane: {size: NARROW_PANE_SIZE}}}]},
    };

    return {type: 'highcharts', options};
};

export default renderRadar;
